import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';

import { ownerApi } from '../api/owner';
import { ApiError } from '../api/client';
import { QuotaBanner } from '../components/QuotaBanner';
import { SuspendedBanner } from '../components/SuspendedBanner';
import type { OwnerBusiness, QuotaStatus } from '../types';

type FormFields = Pick<
  OwnerBusiness,
  | 'tagline'
  | 'description'
  | 'address_line'
  | 'pincode'
  | 'phone'
  | 'whatsapp'
  | 'email'
  | 'website_url'
  | 'instagram_url'
  | 'facebook_url'
>;

const FIELD_LABELS: Array<{ key: keyof FormFields; label: string; type?: string }> = [
  { key: 'tagline', label: 'Tagline' },
  { key: 'address_line', label: 'Address' },
  { key: 'pincode', label: 'Pincode' },
  { key: 'phone', label: 'Phone' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'website_url', label: 'Website URL' },
  { key: 'instagram_url', label: 'Instagram URL' },
  { key: 'facebook_url', label: 'Facebook URL' },
];

export function BusinessProfile() {
  const [business, setBusiness] = useState<OwnerBusiness | null>(null);
  const [quota, setQuota] = useState<QuotaStatus | null>(null);
  const [form, setForm] = useState<FormFields | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [b, q] = await Promise.all([ownerApi.getBusiness(), ownerApi.getQuota()]);
        setBusiness(b);
        setQuota(q);
        setForm({
          tagline: b.tagline,
          description: b.description,
          address_line: b.address_line,
          pincode: b.pincode,
          phone: b.phone,
          whatsapp: b.whatsapp,
          email: b.email,
          website_url: b.website_url,
          instagram_url: b.instagram_url,
          facebook_url: b.facebook_url,
        });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleChange = (key: keyof FormFields, value: string) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form) return;
    setSaving(true);
    setMessage(null);
    try {
      await ownerApi.updateBusiness(form);
      const q = await ownerApi.getQuota();
      setQuota(q);
      setMessage({ type: 'success', text: 'Business profile updated.' });
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setMessage({ type: 'error', text: 'Monthly edit limit reached. Try again next month.' });
      } else if (err instanceof ApiError && err.status === 403) {
        setMessage({ type: 'error', text: 'Business is suspended — cannot save changes.' });
      } else {
        setMessage({ type: 'error', text: 'Could not save changes.' });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) return <p className="text-sm text-slate-500">Loading…</p>;

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-slate-900">Business Profile</h1>

      {business?.is_suspended && <SuspendedBanner />}
      {quota && <QuotaBanner quota={quota} />}

      <form onSubmit={handleSubmit} className="space-y-4 rounded-brand border border-slate-200 bg-white p-6">
        {FIELD_LABELS.map(({ key, label, type }) => (
          <div key={key}>
            <label className="block text-sm font-medium text-slate-700">{label}</label>
            <input
              type={type ?? 'text'}
              value={form[key] ?? ''}
              onChange={(e) => handleChange(key, e.target.value)}
              disabled={business?.is_suspended}
              className="mt-1 w-full rounded-brand border border-slate-300 px-3 py-2 text-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary disabled:bg-slate-100"
            />
          </div>
        ))}

        <div>
          <label className="block text-sm font-medium text-slate-700">Description</label>
          <textarea
            rows={4}
            value={form.description ?? ''}
            onChange={(e) => handleChange('description', e.target.value)}
            disabled={business?.is_suspended}
            className="mt-1 w-full rounded-brand border border-slate-300 px-3 py-2 text-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary disabled:bg-slate-100"
          />
        </div>

        {message && (
          <p className={`text-sm ${message.type === 'success' ? 'text-emerald-600' : 'text-red-600'}`}>
            {message.text}
          </p>
        )}

        <button
          type="submit"
          disabled={saving || business?.is_suspended || (quota ? quota.remaining <= 0 : false)}
          className="rounded-brand bg-brand-primary px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </form>
    </div>
  );
}
